<template>
  <div
    class="ops-card"
    :class="classes"
    :style="tokenStyle"
    data-test="ops-card"
    :data-size="size"
    :data-level="card.level"
    :data-column="card.column"
  >
    <!-- The provider's colour. The whole-card border is the wait threshold; the stripe is always the provider. -->
    <span class="ops-card__stripe" :style="{ background: stripeColor }" aria-hidden="true"></span>

    <div class="ops-card__top">
      <span class="ops-card__dot" :style="{ background: dotColor }" :title="statusTitle" data-test="ops-card-dot"></span>
      <span class="ops-card__name" data-test="ops-card-name">{{ displayName }}</span>
      <span v-if="birthday && isXl" class="ops-card__cake" title="Birthday today" data-test="ops-card-birthday">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" /><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" /><path d="M2 21h20" /><path d="M7 8v3M12 8v3M17 8v3" /></svg>
      </span>
      <span v-if="isXl" class="ops-card__time" data-test="ops-card-time">{{ timeText }}</span>

      <!-- Send alert: a teal bell on every card. The page pre-fills the patient's room. -->
      <button
        v-if="hasBell"
        type="button"
        class="ops-card__bell"
        aria-label="Send alert"
        title="Send alert"
        data-test="ops-card-ping"
        @click.stop="$emit('ping', card)"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
      </button>
      <button
        v-if="hasMenu"
        type="button"
        class="ops-card__more"
        aria-label="More actions"
        title="More actions"
        :aria-expanded="externalMenu ? null : (menuOpen ? 'true' : 'false')"
        data-test="ops-card-menu-btn"
        @click.stop="onMenuButton"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></svg>
      </button>
    </div>

    <div class="ops-card__meta">
      <span class="ops-card__proc" data-test="ops-card-proc">{{ proc }}</span>
      <span v-if="birthday && !isXl" class="ops-card__cake" title="Birthday today" data-test="ops-card-birthday">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" /><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" /><path d="M2 21h20" /><path d="M7 8v3M12 8v3M17 8v3" /></svg>
      </span>
      <span v-if="!isXl && timeText" class="ops-card__time ops-card__time--low" data-test="ops-card-time">{{ timeText }}</span>
    </div>

    <!-- The appointment note can be clinical: never at xl (the wall, seen by passers-by), and not at sm (no room). -->
    <div v-if="hasNote" class="ops-card__note" data-test="ops-card-note">{{ note }}</div>

    <div class="ops-card__timerow">
      <span v-if="timer" class="ops-card__timer" data-test="ops-card-timer">{{ timer }}</span>
      <span v-for="tag in tags" :key="tag.key" class="ops-card__tag" :data-test="`ops-card-tag-${tag.key}`">{{ tag.text }}</span>
      <template v-if="isXl && alertKeys.length">
        <span class="ops-card__spacer"></span>
        <span
          v-for="key in alertKeys"
          :key="key"
          class="ops-card__chip"
          :data-test="`ops-card-alert-${key}`"
        ><AlertBadge :alert-key="key" :detail="detailFor(key)" /></span>
      </template>
    </div>

    <div v-if="!isXl && alertKeys.length" class="ops-card__chips" data-test="ops-card-alerts">
      <!-- A read-only card (the Wall) draws its icons as plain spans: there is no button in it at all. -->
      <component
        :is="interactive ? 'button' : 'span'"
        v-for="key in alertKeys"
        :key="key"
        :type="interactive ? 'button' : null"
        class="ops-card__chip"
        :class="{ 'ops-card__chip--btn': interactive }"
        :data-test="`ops-card-alert-${key}`"
        @click.stop="onChip(key, $event)"
      ><AlertBadge :alert-key="key" :detail="detailFor(key)" /></component>
    </div>

    <!-- The ⋯ menu opens INSIDE the card, so it never clips inside a scrolling column. -->
    <div v-if="menuOpen && hasMenu" class="ops-card__menu" data-test="ops-card-menu">
      <template v-for="item in menuItems">
        <div v-if="item.kind === 'head'" :key="`h-${item.label}`" class="ops-card__menu-head">{{ item.label }}</div>
        <button
          v-else
          :key="`${item.kind}-${item.id || ''}`"
          type="button"
          class="ops-card__menu-item"
          :class="{ 'ops-card__menu-item--sep': item.sep, 'ops-card__menu-item--danger': item.danger }"
          :data-test="`ops-card-menu-${item.kind}${item.id ? `-${item.id}` : ''}`"
          @click.stop="onMenuItem(item)"
        >{{ item.label }}</button>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * The ONE Practice Ops patient card (plan "Design review" → Patient card spec). Board, Operatory, Phone and Wall all
 * draw this component: `size` picks one of five sizes (the design's `Z` table, ported to the SCSS map below),
 * `dark` the Wall's dark theme.
 *
 *   sm     > 9 rooms (compact: no tags, no note)
 *   md     the desktop Board
 *   touch  the phone, and the operatory iPad's queue
 *   lg     the operatory iPad's room card
 *   xl     the Wall: time top-right, NO bell / menu / note, the alert icons inline on the timer row
 *
 * PRESENTATIONAL. Props in, events out — the page owns every write:
 *   ping(card)                          the teal bell ("Send alert"); the page pre-fills the patient's room
 *   alert-click({ card, key, el })      an alert icon was tapped; the page opens the visit checklist
 *   move({ card, to })                  ⋯ → Move to…  (`to` is 'checkin' or an OperatoryNum)
 *   menu(card)                          only with `external-menu`: the ⋯ was tapped and the view draws the menu itself
 *   checkout(card) / dismiss(card)      ⋯ → Check out / Dismiss
 *
 * THE ICONS ARE SCHEDULE VIEW'S, NOT OURS. A card draws `AlertBadge` for blockAlertKeys(payload, patNum,
 * viewSettings, ALERT_KEYS) — Schedule View's own rule, in Schedule View's order, all eleven alerts. There is no
 * Practice Ops alert logic; icons, tooltips and order come from ALERT_META through AlertBadge
 * (cypress/node-tests/practice-ops-alert-order.test.js).
 *
 * THE WORDS (timer, tags, time) come from utils/practiceOps/boardText.js; the threshold border and the timer tone
 * come from board.js (`card.level`, `card.tone`) — computed from timestamps, never from a stored badge.
 * COLOURS: every colour is a token from utils/practiceOps/viewPref.js WALL_THEMES (light and dark), set once as CSS
 * custom properties on the root, so no hex lives in the stylesheet except the size table.
 *
 * ⛔ No prop may be named `patNum`, `siteID`, `loading` or `allSettings`: mixin.js defines computeds with those names
 * and a computed SILENTLY REPLACES a same-named prop (AlertBadge.vue explains it).
 */
import AlertBadge from '@/pages/ScheduleView/components/AlertBadge.vue';
import { ALERT_KEYS } from '@/utils/scheduleView/settingsForm';
import { blockAlertKeys, alertsForPatient } from '@/utils/scheduleView/alerts';
import { odColorToHex, isTooLight } from '@/utils/scheduleView/provColor';
import { WALL_THEMES } from '@/utils/practiceOps/viewPref';
import { cardLastFirst } from '@/utils/practiceOps/moves';
import { clockText, timerText, cardTags } from '@/utils/practiceOps/boardText';

const SIZES = ['sm', 'md', 'touch', 'lg', 'xl'];

export default {
  name: 'OpsPatientCard',

  components: { AlertBadge },

  props: {
    /** A card from board.js buildBoard (checkin / rooms[].cards / checkout). */
    card: { type: Object, required: true },
    size: { type: String, default: 'md', validator: v => SIZES.indexOf(v) !== -1 },
    dark: { type: Boolean, default: false },
    /** The provider's colour ('#rrggbb'); empty ⇒ the token's neutral stripe. */
    stripe: { type: String, default: '' },
    /** Procedures and the appointment note — the page has already preferred the live text (apptDetails.js). */
    proc: { type: String, default: '' },
    note: { type: String, default: '' },
    /** The practice's IANA zone (GENERAL_TIMEZONE) for every clock time on the card. */
    zone: { type: String, default: '' },
    /** The day's /alerts payload (the store's alertsForDate), and the office's switches ({ alerts }). */
    alertPayload: { type: Object, default: null },
    viewSettings: { type: Object, default: null },
    /** False when the office cannot have icons (the gate in utils/practiceOps/alertGate.js). */
    showAlertIcons: { type: Boolean, default: true },
    birthday: { type: Boolean, default: false },
    /** False draws a read-only card (no bell, no menu, no tappable icons). The Wall is never interactive (size xl). */
    interactive: { type: Boolean, default: true },
    /** Where "Move to…" can send this card: [{ id: 'checkin' | OperatoryNum, label }] — the free rooms and Check-in. */
    moveTargets: { type: Array, default: () => [] },
    /** Dismiss is offered only where the page can do it (a patient already in Check-out). */
    canDismiss: { type: Boolean, default: false },
    /** True: the ⋯ button does not open the inline menu — it emits menu(card) and the view draws its own (the Phone's bottom sheet). */
    externalMenu: { type: Boolean, default: false },
  },

  data() {
    return { menuOpen: false };
  },

  computed: {
    isXl() {
      return this.size === 'xl';
    },

    tokens() {
      return WALL_THEMES[this.dark ? 'dark' : 'light'];
    },

    classes() {
      return [
        `ops-card--${this.size}`,
        { 'ops-card--dark': this.dark },
        { 'ops-card--amber': this.card.level === 'amber' },
        { 'ops-card--red': this.card.level === 'red' },
        { 'ops-card--dim': !!this.card.dismissed },
      ];
    },

    /** Every colour, once, as custom properties. The timer chip's tone comes from board.js (`card.tone`). */
    tokenStyle() {
      const t = this.tokens;
      const tones = {
        n: [t.chipNeutralBg, t.chipNeutralText],
        a: [t.chipAmberBg, t.chipAmberText],
        r: [t.chipRedBg, t.chipRedText],
        s: [t.chipSeatedBg, t.chipSeatedText],
        d: [t.chipDismissedBg, t.chipDismissedText],
      };
      const tone = tones[this.card.tone] || tones.n;
      return {
        '--ops-surf': t.cardBg,
        '--ops-edge': t.border,
        '--ops-ink': t.text,
        '--ops-sub': t.textSub,
        '--ops-muted': t.textMuted,
        '--ops-chip': t.chipNeutralBg,
        '--ops-amber': t.amberBorder,
        '--ops-red': t.redBorder,
        '--ops-tone-bg': tone[0],
        '--ops-tone-fg': tone[1],
        '--ops-bell-bg': t.chipSeatedBg,
        '--ops-bell-fg': t.chipSeatedText,
        '--ops-bell-edge': this.dark ? t.accent : '#b9dccd',
        '--ops-accent': t.accent,
      };
    },

    stripeColor() {
      const hex = typeof this.stripe === 'string' && /^#[0-9a-f]{6}$/i.test(this.stripe) ? this.stripe : '';
      return hex && !isTooLight(hex) ? hex : this.tokens.stripeFallback;
    },

    /** The Open Dental status colour (the office's own), else the token for the card's status. */
    dotColor() {
      const own = odColorToHex(this.card.checkinColor);
      if (own && !isTooLight(own)) return own;
      const t = this.tokens;
      const byStatus = {
        arrived: t.dotArrived, ready: t.dotReady, inTreatment: t.dotInTreatment, dismissed: t.dotDismissed,
      };
      return byStatus[this.card.status] || t.dotUnconfirmed;
    },

    statusTitle() {
      const names = { arrived: 'Arrived', ready: 'Ready', inTreatment: 'In treatment', dismissed: 'Dismissed' };
      return `Open Dental status: ${this.card.checkinStatus || names[this.card.status] || ''}`.trim();
    },

    displayName() {
      return cardLastFirst(this.card);
    },

    timeText() {
      return clockText(this.card.scheduledMs, this.zone);
    },

    timer() {
      return timerText(this.card, this.zone);
    },

    tags() {
      return cardTags(this.card, this.zone, this.size);
    },

    hasNote() {
      return !!this.note && this.size !== 'xl' && this.size !== 'sm';
    },

    hasBell() {
      return this.interactive && !this.isXl && !this.card.dismissed;
    },

    hasMenu() {
      return this.interactive && !this.isXl && this.menuItems.length > 0;
    },

    /** The icons, in Schedule View's order and under Schedule View's rule. Nothing when the office cannot have them. */
    alertKeys() {
      if (!this.showAlertIcons || !this.alertPayload) return [];
      return blockAlertKeys(this.alertPayload, this.card.patNum, this.viewSettings, ALERT_KEYS);
    },

    alertDetails() {
      return alertsForPatient(this.alertPayload, this.card.patNum);
    },

    /** Where this card is now, in the ids the page moves by: 'checkin', 'checkout' or the OperatoryNum. */
    selfId() {
      if (this.card.column === 'room') return String(this.card.opNum);
      return this.card.column === 'checkout' ? 'checkout' : 'checkin';
    },

    menuItems() {
      const items = [];
      const targets = (this.moveTargets || []).filter(t => t && String(t.id) !== this.selfId);
      if (targets.length) {
        items.push({ kind: 'head', label: 'Move to…' });
        targets.forEach(t => items.push({ kind: 'move', id: String(t.id), label: t.label }));
      }
      if (this.card.column !== 'checkout') items.push({ kind: 'checkout', label: 'Check out', sep: true });
      if (this.canDismiss && !this.card.dismissed) {
        items.push({ kind: 'dismiss', label: 'Dismiss', danger: true, sep: this.card.column === 'checkout' });
      }
      return items;
    },
  },

  methods: {
    detailFor(key) {
      const d = this.alertDetails[key];
      return d && typeof d === 'object' ? d : {};
    },

    onChip(key, event) {
      if (!this.interactive) return;
      this.$emit('alert-click', { card: this.card, key, el: event && event.currentTarget ? event.currentTarget : null });
    },

    onMenuButton() {
      if (this.externalMenu) this.$emit('menu', this.card);
      else this.menuOpen = !this.menuOpen;
    },

    onMenuItem(item) {
      this.menuOpen = false;
      if (item.kind === 'move') this.$emit('move', { card: this.card, to: item.id });
      else if (item.kind === 'checkout') this.$emit('checkout', this.card);
      else if (item.kind === 'dismiss') this.$emit('dismiss', this.card);
    },
  },
};
</script>

<style lang="scss" scoped>
// The design's `Z` size table (Patient Card.dc.html), ported. `chip` is the alert chip box, `chip-icon` its icon,
// `bell` / `bell-icon` the Send-alert button (PB), `more` the ⋯ button (Z.mb). Text is px: the app root is 14px.
$ops-sizes: (
  sm: (name: 13px, meta: 12px, note: 12px, tag: 11px, timer: 11.5px, icon: 11px, chip: 18px, dot: 7px, radius: 10px, pad: 8px 8px 8px 12px, gap: 4px, stripe: 4px, more: 20px, gap2: 3px, bell: 20px, bell-icon: 11px),
  md: (name: 14px, meta: 12.5px, note: 12px, tag: 11.5px, timer: 12px, icon: 13px, chip: 20px, dot: 8px, radius: 12px, pad: 10px 8px 10px 13px, gap: 6px, stripe: 4px, more: 22px, gap2: 4px, bell: 22px, bell-icon: 12px),
  touch: (name: 15px, meta: 13px, note: 12.5px, tag: 12px, timer: 12.5px, icon: 17px, chip: 30px, dot: 9px, radius: 14px, pad: 12px 12px 12px 16px, gap: 7px, stripe: 5px, more: 40px, gap2: 6px, bell: 40px, bell-icon: 18px),
  lg: (name: 22px, meta: 15px, note: 14px, tag: 13px, timer: 15px, icon: 20px, chip: 28px, dot: 11px, radius: 14px, pad: 16px 18px 16px 22px, gap: 9px, stripe: 6px, more: 36px, gap2: 8px, bell: 36px, bell-icon: 18px),
  xl: (name: 28px, meta: 20px, note: 18px, tag: 18px, timer: 24px, icon: 26px, chip: 34px, dot: 14px, radius: 16px, pad: 14px 18px 14px 24px, gap: 8px, stripe: 7px, more: 0, gap2: 10px, bell: 0, bell-icon: 0),
);

@keyframes opsPulse {
  0%,
  100% { box-shadow: 0 0 0 0 rgba(229, 72, 77, 0.45); }
  50% { box-shadow: 0 0 0 6px rgba(229, 72, 77, 0); }
}

.ops-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
  background: var(--ops-surf);
  border: 1px solid var(--ops-edge);
  color: var(--ops-ink);
  font-family: inherit;
  text-align: left;
  // the Wall's dark card has no shadow; the light card has the design's hairline
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);
}

.ops-card--dark { box-shadow: none; }
.ops-card--dim { opacity: 0.55; }

// The WHOLE-CARD threshold border. Amber at 1x the office's wait limit, red at 2x; red pulses, it never flashes.
.ops-card--amber { border: 1.5px solid var(--ops-amber); }
.ops-card--red {
  border: 1.5px solid var(--ops-red);
  animation: opsPulse 2.4s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .ops-card--red { animation: none; }
}

.ops-card__stripe {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
}

.ops-card__top {
  display: flex;
  align-items: flex-start;
  min-width: 0;
}

.ops-card__dot {
  flex: none;
  border-radius: 50%;
}

.ops-card__name {
  flex: 1;
  min-width: 0;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ops-card__time {
  flex: none;
  color: var(--ops-muted);
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
}

.ops-card__cake {
  display: flex;
  align-self: center;
  flex: none;
  color: var(--ops-muted);
}

.ops-card__meta {
  display: flex;
  align-items: baseline;
  min-width: 0;
  line-height: 1.3;
}

.ops-card__proc {
  flex: 1;
  min-width: 0;
  color: var(--ops-sub);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ops-card__time--low { margin-left: 6px; }

.ops-card__note {
  color: var(--ops-muted);
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.ops-card__timerow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.ops-card__timer {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: var(--ops-tone-bg);
  color: var(--ops-tone-fg);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ops-card__tag {
  color: var(--ops-sub);
  background: var(--ops-chip);
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.ops-card__spacer { flex: 1; }

.ops-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.ops-card__chip {
  display: grid;
  place-items: center;
  border: 0;
  padding: 0;
  background: var(--ops-chip);
  color: var(--ops-sub);

  // AlertBadge is Schedule View's 15px icon box; the chip scales it to fill the box and carries the colour itself.
  ::v-deep .sv-badge {
    width: 100%;
    height: 100%;
    background: transparent;
    color: inherit;
    border-radius: inherit;
  }
}

button.ops-card__chip { cursor: pointer; }
button.ops-card__chip:disabled { cursor: default; }
button.ops-card__chip:not(:disabled):hover { filter: brightness(0.96); }

.ops-card__bell {
  flex: none;
  display: grid;
  place-items: center;
  padding: 0;
  margin: -3px 0 -3px 2px;
  border: 1px solid var(--ops-bell-edge);
  border-radius: 8px;
  background: var(--ops-bell-bg);
  color: var(--ops-bell-fg);
  cursor: pointer;

  &:hover { border-color: var(--ops-accent); filter: brightness(0.97); }
}

.ops-card__more {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: -4px -4px -4px 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--ops-muted);
  cursor: pointer;

  &:hover { background: var(--ops-chip); }
}

.ops-card__bell svg,
.ops-card__more svg,
.ops-card__cake svg { display: block; flex: none; }

.ops-card__menu {
  margin: 2px -4px -2px;
  padding-top: 6px;
  border-top: 1px solid var(--ops-edge);
  display: flex;
  flex-direction: column;
}

.ops-card__menu-head {
  padding: 4px 8px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ops-muted);
}

.ops-card__menu-item {
  text-align: left;
  border: 0;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--ops-ink);
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;

  &:hover { background: var(--ops-chip); }
}

.ops-card__menu-item--sep { border-top: 1px solid var(--ops-edge); border-radius: 0 0 6px 6px; }
.ops-card__menu-item--danger { color: #c2333a; }
.ops-card--dark .ops-card__menu-item--danger { color: #ff8589; }

// Each size: one block from the map.
@each $size, $z in $ops-sizes {
  .ops-card--#{$size} {
    border-radius: map-get($z, radius);
    padding: map-get($z, pad);
    gap: map-get($z, gap);

    .ops-card__stripe { width: map-get($z, stripe); }
    .ops-card__top { gap: map-get($z, gap2); }
    .ops-card__dot {
      width: map-get($z, dot);
      height: map-get($z, dot);
      margin-top: round(map-get($z, name) * 0.6 - map-get($z, dot) * 0.5);
    }
    .ops-card__name { font-size: map-get($z, name); }
    .ops-card__time { font-size: map-get($z, meta); }
    .ops-card__meta { font-size: map-get($z, meta); }
    .ops-card__note { font-size: map-get($z, note); }
    .ops-card__timerow { gap: map-get($z, gap2); }
    .ops-card__timer {
      font-size: map-get($z, timer);
      height: round(map-get($z, timer) * 1.8);
      padding: 0 round(map-get($z, timer) * 0.62);
    }
    .ops-card__tag {
      font-size: map-get($z, tag);
      padding: round(map-get($z, tag) * 0.25) round(map-get($z, tag) * 0.6);
    }
    .ops-card__cake svg { width: map-get($z, icon); height: map-get($z, icon); }
    .ops-card__chip {
      width: map-get($z, chip);
      height: map-get($z, chip);
      border-radius: round(map-get($z, chip) * 0.28);

      ::v-deep .sv-badge { font-size: map-get($z, icon); }
    }
    .ops-card__bell {
      width: map-get($z, bell);
      height: map-get($z, bell);

      svg { width: map-get($z, bell-icon); height: map-get($z, bell-icon); }
    }
    .ops-card__more {
      width: map-get($z, more);
      height: map-get($z, more);

      svg { width: round(map-get($z, icon) * 1.1); height: round(map-get($z, icon) * 1.1); }
    }
  }
}
</style>
